import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Link2, Plus, Unlink, Copy } from "lucide-react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Chip } from "@/components/ui/chip";
import { Kicker } from "@/components/ui/kicker";
import { Skeleton } from "@/components/ui/skeleton";
import { Timestamp } from "@/components/ui/timestamp";
import {
  GithubIcon,
  JiraIcon,
  AzureDevOpsIcon,
  LinearIcon,
} from "@/components/icons";
import { canonicalStateChip } from "@/components/finding-badges";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { WorkRequestChip } from "@/components/experience/work-request-chip";
import { CreateIssueDialog, LinkExistingDialog } from "./create-issue-dialog";
import { linkNoun, linkStateChip, stateLabel } from "./workflow-labels";

export function ProviderIcon({
  provider,
  className,
}: {
  provider?: string;
  className?: string;
}) {
  if (provider === "jira") {
    return (
      <JiraIcon
        className={className || "h-4 w-4 shrink-0 text-blue-500 fill-current"}
        aria-hidden="true"
      />
    );
  }
  if (provider === "azure_devops") {
    return (
      <AzureDevOpsIcon
        className={className || "h-4 w-4 shrink-0 text-blue-600 fill-current"}
        aria-hidden="true"
      />
    );
  }
  if (provider === "linear") {
    return (
      <LinearIcon
        className={className || "h-4 w-4 shrink-0 text-purple-500 fill-current"}
        aria-hidden="true"
      />
    );
  }
  return (
    <GithubIcon
      className={className || "h-4 w-4 shrink-0 text-muted-foreground"}
      aria-hidden="true"
    />
  );
}

export function providerDisplayName(provider?: string) {
  if (provider === "jira") return "Jira";
  if (provider === "azure_devops") return "Azure DevOps";
  if (provider === "linear") return "Linear";
  return "GitHub";
}

const UNASSIGNED = "__unassigned__";

interface WorkflowRailProps {
  canonicalId: string;
  projectId: string;
  stateReason?: string | null;
  canonicalState?: string | null;
}

/**
 * Workflow rail on a canonical finding: status, assignee and linked
 * tracker work. Problems (hinders, missing, incorrect) get the full
 * rail; strengths only show their status — nobody owns praise.
 */
export function WorkflowRail({
  canonicalId,
  projectId,
  stateReason,
  canonicalState,
}: WorkflowRailProps) {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.workflow.get.useQuery(
    { canonicalId },
    // Poll while an issue is being created so "Creating…" resolves.
    {
      refetchInterval: (q) =>
        q.state.data?.links.some((l) => l.creationState === "pending")
          ? 3000
          : false,
    },
  );
  const requests = useWorkRequests(projectId);
  const openRequest = requests.byCanonical.get(canonicalId) ?? null;
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [copying, setCopying] = useState(false);

  const handleCopyMarkdown = async () => {
    try {
      setCopying(true);
      const preview = await utils.workflow.previewIssue.fetch({
        canonicalId,
        sections: { personaQuote: true, evidence: true },
      });
      const text = `# ${preview.title}\n\n${preview.body}`;
      await navigator.clipboard.writeText(text);
      toast.success("Ticket markdown copied to clipboard");
    } catch (err: unknown) {
      toastTrpcError(err, "Failed to copy ticket markdown");
    } finally {
      setCopying(false);
    }
  };

  const invalidate = () => {
    utils.workflow.get.invalidate({ canonicalId });
    utils.workflow.timeline.invalidate({ canonicalId });
    utils.finding.list.invalidate({ projectId });
    utils.finding.queueCounts.invalidate({ projectId });
  };
  const unlink = trpc.workflow.unlink.useMutation({
    onSuccess: invalidate,
    onError: (err) => toastTrpcError(err, "Failed to unlink tracker issue"),
  });
  const retry = trpc.workflow.createIssue.useMutation({
    onSuccess: invalidate,
    onError: (err) => {
      toastTrpcError(err, "Failed to create issue");
      invalidate();
    },
  });

  if (isLoading || !data) {
    return (
      <aside aria-label="Workflow" className="space-y-3">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </aside>
    );
  }

  const effectiveState = (canonicalState ?? data.state) as any;
  const stateChip = canonicalStateChip(effectiveState, stateReason);
  const repo = data.github.repo;
  const isTrackerConfigured =
    (data as any).tracker?.configured || (data.github.configured && !!repo);
  const canTicket = data.canEdit && isTrackerConfigured;
  const canLink =
    data.canEdit &&
    (Boolean((data as any).tracker?.configured) || data.github.configured);

  return (
    <aside
      aria-labelledby="workflow-heading"
      className="space-y-5 rounded-md border border-border bg-card p-5"
    >
      <h2 id="workflow-heading" className="text-md font-semibold">
        Workflow
      </h2>

      <dl className="space-y-4">
        <div className="space-y-1">
          <dt>
            <Kicker>Status</Kicker>
          </dt>
          <dd className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              {stateChip ? (
                <Chip tone={stateChip.tone}>{stateChip.label}</Chip>
              ) : (
                <span className="text-sm">{stateLabel(effectiveState)}</span>
              )}
              {openRequest && <WorkRequestChip request={openRequest} />}
            </div>
            {data.snoozeUntil && (
              <p className="text-xs text-muted-foreground">
                Snoozed until <Timestamp date={data.snoozeUntil} />
              </p>
            )}
          </dd>
        </div>
      </dl>

      {data.isProblem && (
        <section aria-labelledby="linked-work-heading" className="space-y-3">
          <Kicker as="h3">
            <span id="linked-work-heading">Linked work</span>
          </Kicker>
          {data.links.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No issue or pull request yet.
            </p>
          ) : (
            <ul className="divide-y divide-border/60">
              {data.links.map((link) => {
                const chip = linkStateChip(link);
                const label =
                  `${linkNoun(link.kind)} ${link.displayKey ?? ""}`.trim();
                return (
                  <li key={link.id} className="space-y-1 py-2 first:pt-0">
                    <div className="flex items-center gap-2">
                      <ProviderIcon
                        provider={link.provider}
                        className="h-4 w-4 shrink-0"
                      />
                      {link.url && link.creationState === "created" ? (
                        <SimpleTooltip content={link.title}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="min-w-0 truncate text-sm font-medium underline-offset-4 hover:underline"
                          >
                            {label}
                            <span className="sr-only">
                              {" "}
                              ({`opens ${providerDisplayName(link.provider)}`})
                            </span>
                          </a>
                        </SimpleTooltip>
                      ) : (
                        <span className="text-sm font-medium">
                          {link.creationState === "created"
                            ? label
                            : "New issue"}
                        </span>
                      )}
                      <Chip tone={chip.tone}>{chip.label}</Chip>
                      <span className="flex-1" />
                      {data.canEdit && link.creationState !== "pending" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Unlink ${label}`}
                          onClick={() =>
                            unlink.mutate({ canonicalId, linkId: link.id })
                          }
                          disabled={unlink.isPending}
                        >
                          <Unlink className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                    {link.title && link.creationState === "created" && (
                      <p className="truncate text-xs text-muted-foreground">
                        {link.title}
                      </p>
                    )}
                    {link.syncError ? (
                      <p className="text-xs text-severity-critical">
                        {link.creationState === "failed"
                          ? `${providerDisplayName(link.provider)} did not create the issue: `
                          : `Can't reach ${providerDisplayName(link.provider)}: `}
                        {link.syncError}
                      </p>
                    ) : (
                      link.syncedAt && (
                        <p className="text-xs text-muted-foreground">
                          Synced <Timestamp date={link.syncedAt} />
                        </p>
                      )
                    )}
                    {link.creationState === "failed" && canTicket && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => retry.mutate({ canonicalId })}
                        disabled={retry.isPending}
                      >
                        Retry
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {data.canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant={canTicket ? "outline" : "ghost"}
                size="sm"
                onClick={() => setCreating(true)}
                disabled={!canTicket}
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Create issue
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLinking(true)}
                disabled={!canLink}
              >
                <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Link existing
              </Button>
              <Button
                variant={!canTicket ? "outline" : "ghost"}
                size="sm"
                onClick={handleCopyMarkdown}
                disabled={copying}
              >
                <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
                {copying ? "Copying…" : "Copy markdown"}
              </Button>
            </div>
          )}
          {data.canEdit && !isTrackerConfigured && (
            <div className="rounded-md border border-border/80 bg-muted/40 p-3 space-y-1.5 text-xs">
              <p className="font-medium text-foreground">
                Issue tracker not connected
              </p>
              <p className="text-muted-foreground">
                Use &ldquo;Copy markdown&rdquo; above to file this ticket
                manually in Jira, Linear, or GitHub, or configure a tracker in{" "}
                <Link
                  to={`/projects/${projectId}/settings`}
                  className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
                >
                  project settings
                </Link>
                .
              </p>
            </div>
          )}
          {data.canEdit &&
            isTrackerConfigured &&
            !repo &&
            !(data as any).tracker?.configured && (
              <p className="text-xs text-muted-foreground">
                Link a GitHub repository to this project to create issues.{" "}
                <Link
                  to={`/projects/${projectId}/settings`}
                  className="text-foreground underline underline-offset-4"
                >
                  Project settings
                </Link>
              </p>
            )}
          {repo && (
            <p className="text-xs text-muted-foreground">
              Issues go to {repo}. Closing one marks this “awaiting re-run”;
              only a re-run verifies the fix.
            </p>
          )}
        </section>
      )}

      {creating && (
        <CreateIssueDialog
          canonicalId={canonicalId}
          open={creating}
          onOpenChange={setCreating}
        />
      )}
      {linking && (
        <LinkExistingDialog
          canonicalId={canonicalId}
          open={linking}
          onOpenChange={setLinking}
        />
      )}
    </aside>
  );
}
